import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { EnumLabelService } from './services/enum-label/enum-label.service';

/**
 * Displays an enum value in the active language.
 * Usage: {{ order.orderStatusTypeID | enumLabel:'orderStatusType' }}
 *        {{ order.orderTypeID | enumLabel:'orderType':order.orderType?.name }}   (API name as fallback)
 */
@Pipe({
  name: 'enumLabel',
  pure: false,
  standalone: false
})
export class EnumLabelPipe implements PipeTransform, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  private readonly langSub: Subscription;
  private lastArgs: unknown[] | null = null;
  private lastLang: string | null = null;
  private lastResult = '';

  constructor() {
    const cdr = inject(ChangeDetectorRef);
    // Re-render OnPush hosts when the language changes
    this.langSub = this.transloco.langChanges$.subscribe(() => cdr.markForCheck());
  }

  transform(value: string | number | boolean | null | undefined, enumName: string, fallbackName?: string | null): string {
    const lang = this.transloco.getActiveLang();
    const args = [value, enumName, fallbackName];
    // Cheap memoization: the pipe is impure so it runs every change detection
    if (this.lastArgs && lang === this.lastLang && args.every((a, i) => a === this.lastArgs![i])) {
      return this.lastResult;
    }
    this.lastArgs = args;
    this.lastLang = lang;
    this.lastResult = this.enumLabels.label(enumName, value, fallbackName);
    return this.lastResult;
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
  }
}
